import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../features/auth/AuthContext';
import { getActiveLocale } from '../../../shared/i18n/i18n';
import { voiceApi } from '../../../shared/api/voice';
import { useWorkspaceActions } from '../../../workspace/WorkspaceProvider';
import { AudioCapture } from '../audio/AudioCapture';
import { dispatchVoiceCommand } from '../commands/VoiceCommandDispatcher';
import { IntentEngine } from '../intent/IntentEngine';
import {
  chooseLocalAiRuntime,
  detectLocalAiCapabilities,
  probeLocalAiCapabilities,
  supportsHalfPrecision,
} from '../models/CapabilityDetector';
import { LocalModelManager, type LocalModelStatus } from '../models/LocalModelManager';
import { recordLocalAiDuration } from '../models/ModelTelemetry';
import { normalizeTranscript } from '../normalize/TranscriptNormalizer';
import { resolveSpeechModel, speechAttempts } from '../stt/SpeechModelSelection';
import { SttManager } from '../stt/SttManager';
import type {
  LocalAiCapabilities,
  LocalAiRuntime,
  LocalAiSettings,
  PreparedVoiceCommand,
  VarzaVoiceCommand,
  VoiceCommandState,
  SpeechModel,
  SpokenLanguage,
  VoiceLanguage,
  VoiceListenMode,
  VoiceManifest,
} from '../types/LocalAiTypes';

/*
  Bumped from v1 to force one stored value to be reconsidered — see `readSettings`.

  A default is only a default for somebody who has never used the feature. Everyone who has
  carries a `voiceLanguage` written under the old rule, and no amount of changing the default
  reaches them; the version is what does.
*/
const settingsKey = 'varza.localVoice.settings.v2';
const legacySettingsKey = 'varza.localVoice.settings.v1';
const defaultSettings: LocalAiSettings = {
  enabled: false,
  /*
   * Persian, unconditionally.
   *
   * Not `auto`, because there is nothing behind `auto` but the interface language — Whisper is
   * told which language to hear and does not work it out for itself. And not the interface
   * language either, which is the subtler mistake this replaced: an operator reading English
   * menus in an Iranian accounting system is still speaking Persian into the microphone, and
   * deriving one from the other transcribed their Persian as English. The language of the
   * speech and the language of the labels are two different facts. See `SpokenLanguage`.
   */
  voiceLanguage: 'fa',
  /*
   * Turbo, because it is now a checkpoint every GPU can run.
   *
   * This was briefly Small, on the reasoning that a good many working adapters have no
   * half-precision shaders and would download 537 MB before finding that out. Publishing the
   * fp32 build of turbo alongside the fp16 one removed the premise: those machines get the
   * larger build of the same checkpoint instead of a worse checkpoint. Small remains what a
   * machine with no usable WebGPU is clamped to, which `resolveSpeechModel` decides — and it
   * remains selectable, for a metered connection or a small disk.
   */
  speechModel: 'turbo',
  enhanced: false,
  onlineFallback: 'never',
  continuousSession: false,
  memoryPolicy: 'balanced',
};
const emptyModelStatus: LocalModelStatus = {
  basicInstalled: false,
  enhancedInstalled: false,
  installed: [],
  speech: { turbo: false, small: false },
};

export interface VoiceCommandViewState {
  status: VoiceCommandState;
  transcript: string;
  error: string | undefined;
  prepared: PreparedVoiceCommand | undefined;
  installPrompt: boolean;
  installProgress: number | undefined;
  audioLevel: number;
}

export interface VoiceCommandContextValue {
  tenantReady: boolean;
  settings: LocalAiSettings;
  manifest: VoiceManifest | undefined;
  capabilities: LocalAiCapabilities;
  runtime: 'webgpu' | 'wasm';
  /** The checkpoint this device will actually listen with, which the setting alone cannot say. */
  speechModel: SpeechModel;
  models: LocalModelStatus;
  view: VoiceCommandViewState;
  setSettings: (patch: Partial<LocalAiSettings>) => void;
  toggleListening: (mode?: VoiceListenMode) => Promise<void>;
  runText: (text: string) => Promise<boolean>;
  install: (packageName: 'basic' | 'enhanced') => Promise<void>;
  /**
   * What installing a package will actually cost on this device.
   *
   * A number nobody may work out for themselves. Two speech checkpoints are published and one
   * is fetched; each model is published in more than one weight format and one is fetched. Both
   * choices depend on hardware, so a screen that sums the manifest gets a figure hundreds of
   * megabytes away from the download that follows — in the one place an operator is reading the
   * number before deciding.
   */
  packageBytes: (packageName: 'basic' | 'enhanced') => number;
  /** What one speech checkpoint costs here, for the choice between them. */
  speechModelBytes: (model: SpeechModel) => number;
  clearModels: () => Promise<void>;
  confirm: () => Promise<void>;
  cancel: () => Promise<void>;
  dismiss: () => void;
  testMicrophone: () => Promise<boolean>;
}

const VoiceCommandContext = createContext<VoiceCommandContextValue | null>(null);

export function useVoiceCommand(): VoiceCommandContextValue {
  const value = useContext(VoiceCommandContext);
  if (!value) throw new Error('useVoiceCommand was used outside VoiceCommandProvider.');
  return value;
}

/** Allows shell components to keep rendering in isolation (for previews and focused tests). */
export function useOptionalVoiceCommand(): VoiceCommandContextValue | null {
  return useContext(VoiceCommandContext);
}

export function VoiceCommandProvider({ children }: { children: ReactNode }) {
  const { open } = useWorkspaceActions();
  return (
    <VoiceCommandRuntimeProvider openAddress={open}>
      {children}
    </VoiceCommandRuntimeProvider>
  );
}

/**
 * Shell-neutral local command runtime.
 *
 * Both Varza shells share the model cache, workers, settings and server contract. The only
 * shell-specific operation is opening the route returned by the authoritative API.
 */
export function VoiceCommandRuntimeProvider({
  children,
  openAddress,
}: {
  children: ReactNode;
  openAddress: (address: string) => void;
}) {
  const location = useLocation();
  const { selectedTenant } = useAuth();
  const [settings, updateSettings] = useState(readSettings);
  const [manifest, setManifest] = useState<VoiceManifest>();
  const [models, setModels] = useState<LocalModelStatus>(emptyModelStatus);
  const [view, setView] = useState<VoiceCommandViewState>(() => ({
    status: settings.enabled && selectedTenant !== null ? 'initializing' : 'disabled',
    transcript: '',
    error: undefined,
    prepared: undefined,
    installPrompt: false,
    installProgress: undefined,
    audioLevel: 0,
  }));
  /*
   * The GPU is asked what it can do, once, and the answer replaces the synchronous guess.
   *
   * It has to be an effect because `requestAdapter` is a promise, and the first reading has to
   * be the pessimistic one: half precision is assumed absent until an adapter has confirmed it,
   * so the window before the probe lands can only under-promise. It settles within a frame or
   * two of mount, long before the microphone can be pressed.
   */
  const [capabilities, setCapabilities] = useState<LocalAiCapabilities>(detectLocalAiCapabilities);
  const [runtime, setRuntime] = useState<LocalAiRuntime>(() => chooseLocalAiRuntime(detectLocalAiCapabilities()));
  const halfPrecision = supportsHalfPrecision(capabilities);
  const speechModel = resolveSpeechModel(settings.speechModel, runtime);
  const manager = useMemo(
    () => manifest ? new LocalModelManager(manifest, halfPrecision) : undefined,
    [manifest, halfPrecision],
  );
  const audio = useRef<AudioCapture | undefined>(undefined);
  const stt = useRef<SttManager | undefined>(undefined);
  const intentEngine = useRef<IntentEngine | undefined>(undefined);
  const finishing = useRef(false);
  const listenMode = useRef<VoiceListenMode>('command');

  useEffect(() => {
    let active = true;
    void probeLocalAiCapabilities().then((probed) => {
      if (!active) return;
      setCapabilities(probed);
      setRuntime(chooseLocalAiRuntime(probed));
    });
    return () => { active = false; };
  }, []);

  const refreshModels = useCallback(async () => {
    setModels(manager ? await manager.status(speechModel) : emptyModelStatus);
  }, [manager, speechModel]);

  useEffect(() => {
    if (selectedTenant === null) {
      setManifest(undefined);
      setModels(emptyModelStatus);
      setView((current) => ({
        ...current,
        status: 'disabled',
        error: undefined,
        prepared: undefined,
        installPrompt: false,
        installProgress: undefined,
        audioLevel: 0,
      }));
      return undefined;
    }

    setManifest(undefined);
    setModels(emptyModelStatus);
    setView((current) => ({
      ...current,
      status: 'disabled',
      error: undefined,
      prepared: undefined,
      installPrompt: false,
      installProgress: undefined,
      audioLevel: 0,
    }));
    let active = true;
    voiceApi.manifest().then((value) => {
      if (!active) return;
      if (!isVoiceManifest(value)) throw new Error('The server returned an invalid local voice manifest.');
      setManifest(value);
      setView((current) => ({
        ...current,
        status: settings.enabled && value.features.enabled ? 'idle' : 'disabled',
        ...(!value.features.enabled ? { error: 'Voice commands are disabled by the server policy.' } : {}),
      }));
    }).catch((error: unknown) => {
      if (active) setView((current) => ({ ...current, status: 'error', error: errorMessage(error) }));
    });
    return () => { active = false; };
  }, [selectedTenant?.tenantId]);

  useEffect(() => { void refreshModels(); }, [refreshModels]);

  useEffect(() => () => {
    stt.current?.dispose();
    stt.current = undefined;
    intentEngine.current?.dispose();
    intentEngine.current = undefined;
    const capture = audio.current;
    audio.current = undefined;
    if (capture) void capture.stop();
  }, [selectedTenant?.tenantId]);

  useEffect(() => {
    stt.current?.dispose();
    stt.current = undefined;
  }, [speechModel]);

  const setSettings = useCallback((patch: Partial<LocalAiSettings>) => {
    updateSettings((current) => {
      const next = { ...current, ...patch };
      try {
        localStorage.setItem(settingsKey, JSON.stringify(next));
      } catch {
        // Storage-disabled browsers keep the preference for the current tab.
      }
      setView((viewState) => ({ ...viewState, status: next.enabled ? 'idle' : 'disabled', error: undefined }));
      if (!next.enabled) {
        stt.current?.dispose();
        stt.current = undefined;
        intentEngine.current?.dispose();
        intentEngine.current = undefined;
      }
      return next;
    });
  }, []);

  const install = useCallback(async (packageName: 'basic' | 'enhanced') => {
    if (!manager) throw new Error('The local model manifest is not available.');
    setView((current) => ({ ...current, status: 'initializing', error: undefined, installPrompt: true, installProgress: 0 }));
    try {
      await manager.install(packageName, speechModel, (progress) => {
        setView((current) => ({ ...current, installProgress: progress.progress }));
      });
      if (packageName === 'enhanced') setSettings({ enabled: true, enhanced: true });
      else setSettings({ enabled: true });
      await refreshModels();
      setView((current) => ({ ...current, status: 'idle', installPrompt: false, installProgress: undefined }));
    } catch (error) {
      setView((current) => ({ ...current, status: 'error', error: errorMessage(error), installProgress: undefined }));
    }
  }, [manager, refreshModels, setSettings, speechModel]);

  const packageBytes = useCallback(
    (packageName: 'basic' | 'enhanced') => manager?.packageBytes(packageName, speechModel) ?? 0,
    [manager, speechModel],
  );

  const speechModelBytes = useCallback(
    (model: SpeechModel) => manager?.speechBytes(model) ?? 0,
    [manager],
  );

  const clearModels = useCallback(async () => {
    await manager?.clear();
    stt.current?.dispose();
    stt.current = undefined;
    intentEngine.current?.dispose();
    intentEngine.current = undefined;
    setSettings({ enabled: false, enhanced: false });
    await refreshModels();
  }, [manager, refreshModels, setSettings]);

  const executePrepared = useCallback((prepared: PreparedVoiceCommand) => {
    dispatchVoiceCommand(prepared, openAddress);
    setView((current) => ({ ...current, status: 'success', prepared, error: undefined }));
    globalThis.setTimeout(() => setView((current) => current.status === 'success'
      ? { ...current, status: settings.enabled ? 'idle' : 'disabled', prepared: undefined, transcript: '' }
      : current), 3_000);
  }, [openAddress, settings.enabled]);

  const runText = useCallback(async (
    rawText: string,
    runtimeOverride?: LocalAiRuntime,
    spokenLanguageOverride?: SpokenLanguage,
  ): Promise<boolean> => {
    const text = rawText.trim();
    if (!manifest?.features.enabled || text.length === 0) return false;
    const inferenceRuntime = runtimeOverride ?? runtime;
    const started = performance.now();
    setView((current) => ({ ...current, status: 'understanding', transcript: text, error: undefined, installPrompt: false }));
    try {
      const normalized = normalizeTranscript(text, spokenLanguageOverride);
      intentEngine.current ??= new IntentEngine(manifest, inferenceRuntime, halfPrecision);
      const candidate = await intentEngine.current.understand(
        normalized.normalizedText,
        `${location.pathname}${location.search}`,
        {
          semanticInstalled: models.basicInstalled,
          enhancedInstalled: models.enhancedInstalled,
          enhancedEnabled: settings.enhanced,
        },
      );
      if (!candidate) {
        setView((current) => ({ ...current, status: 'clarifying', error: 'I could not match that command safely. Try being more specific.' }));
        return false;
      }
      const command: VarzaVoiceCommand = {
        schemaVersion: '1.0',
        requestId: crypto.randomUUID(),
        transcript: { raw: text, normalized: normalized.normalizedText, language: normalized.language },
        intent: candidate.intent.id,
        confidence: candidate.confidence,
        source: candidate.source,
        parameters: candidate.parameters,
        context: {
          route: `${location.pathname}${location.search}`,
          pageId: location.pathname,
          ...(document.querySelector('table, [role="grid"]') === null ? {} : { documentState: 'table' }),
        },
        client: {
          localModel: candidate.source === 'local-llm' ? 'qwen3-0.6b' : 'deterministic',
          runtime: inferenceRuntime,
        },
      };
      setView((current) => ({ ...current, status: 'preparing' }));
      const prepareStarted = performance.now();
      const prepared = await voiceApi.prepare(command);
      recordLocalAiDuration('prepare', performance.now() - prepareStarted);
      if (prepared.status === 'needsClarification' || prepared.status === 'forbidden') {
        setView((current) => ({ ...current, status: 'clarifying', prepared, error: prepared.message ?? 'More information is required.' }));
        return false;
      }
      if (prepared.confirmation.required) {
        setView((current) => ({ ...current, status: 'confirming', prepared }));
      } else {
        executePrepared(prepared);
      }
      return true;
    } catch (error) {
      setView((current) => ({ ...current, status: 'error', error: errorMessage(error) }));
      return false;
    } finally {
      recordLocalAiDuration('endToEnd', performance.now() - started);
    }
  }, [executePrepared, halfPrecision, location.pathname, location.search, manifest, models, runtime, settings.enhanced]);

  const finishListening = useCallback(async () => {
    if (!audio.current || finishing.current) return;
    finishing.current = true;
    try {
      setView((current) => ({ ...current, status: 'transcribing', audioLevel: 0 }));
      const capture = audio.current;
      audio.current = undefined;
      const samples = await capture.stop();
      if (samples.length < 1_600) throw new Error('No speech was detected.');
      stt.current ??= new SttManager(
        manifest!,
        speechAttempts(settings.speechModel, runtime, models.speech),
        halfPrecision,
      );
      const language = spokenLanguage(settings.voiceLanguage);
      const transcript = await stt.current.transcribe(samples, language);
      // An empty result here is the worker having thrown away Whisper's answer to silence —
      // see `TranscriptGuard`. Saying so is more use than showing what it invented.
      if (!transcript) throw new Error('No speech was detected.');
      const inferenceRuntime = stt.current.runtime;
      if (inferenceRuntime !== runtime) {
        intentEngine.current?.dispose();
        intentEngine.current = undefined;
        setRuntime(inferenceRuntime);
      }
      if (listenMode.current === 'transcript') {
        setView((current) => ({ ...current, status: 'transcribed', transcript, error: undefined, prepared: undefined }));
        return;
      }
      await runText(transcript, inferenceRuntime, language);
    } catch (error) {
      setView((current) => ({ ...current, status: 'error', error: errorMessage(error) }));
    } finally {
      finishing.current = false;
    }
  }, [halfPrecision, manifest, models.speech, runText, runtime, settings.speechModel, settings.voiceLanguage]);

  const stopCapture = useCallback(async () => {
    const capture = audio.current;
    if (!capture) return;
    audio.current = undefined;
    finishing.current = true;
    try {
      await capture.stop();
    } finally {
      finishing.current = false;
    }
  }, []);

  const toggleListening = useCallback(async (mode: VoiceListenMode = 'command') => {
    if (view.status === 'listening') {
      await finishListening();
      return;
    }
    if (selectedTenant === null) {
      setView((current) => ({ ...current, status: 'disabled', error: undefined, installPrompt: false }));
      return;
    }
    if (!manifest) return;
    if (!manifest.features.enabled) {
      setView((current) => ({ ...current, status: 'error', error: 'Voice commands are unavailable.' }));
      return;
    }
    if (!models.basicInstalled) {
      setView((current) => ({ ...current, installPrompt: true, status: 'disabled', error: undefined }));
      return;
    }
    if (!settings.enabled) setSettings({ enabled: true });
    listenMode.current = mode;
    try {
      const capture = new AudioCapture();
      audio.current = capture;
      await capture.start({
        onLevel: (audioLevel) => setView((current) => ({ ...current, audioLevel })),
        onAutoStop: () => { void finishListening(); },
      });
      setView((current) => ({ ...current, status: 'listening', transcript: '', error: undefined, prepared: undefined }));
    } catch (error) {
      audio.current = undefined;
      const denied = error instanceof DOMException && error.name === 'NotAllowedError';
      setView((current) => ({ ...current, status: 'error', error: denied
        ? 'Microphone access was denied. Allow it in the browser site settings and try again.'
        : errorMessage(error) }));
    }
  }, [finishListening, manifest, models.basicInstalled, selectedTenant, setSettings, settings.enabled, view.status]);

  const confirm = useCallback(async () => {
    if (!view.prepared) return;
    setView((current) => ({ ...current, status: 'executing' }));
    try {
      executePrepared(await voiceApi.execute(view.prepared.commandId));
    } catch (error) {
      setView((current) => ({ ...current, status: 'error', error: errorMessage(error) }));
    }
  }, [executePrepared, view.prepared]);

  const cancel = useCallback(async () => {
    await stopCapture();
    const prepared = view.prepared;
    if (prepared && prepared.status === 'awaitingConfirmation') {
      await voiceApi.cancel(prepared.commandId).catch(() => undefined);
    }
    setView((current) => ({ ...current, status: settings.enabled ? 'idle' : 'disabled', prepared: undefined, transcript: '', error: undefined, installPrompt: false }));
  }, [settings.enabled, stopCapture, view.prepared]);

  const dismiss = useCallback(() => {
    void stopCapture();
    setView((current) => ({ ...current, status: settings.enabled ? 'idle' : 'disabled', prepared: undefined, transcript: '', error: undefined, installPrompt: false }));
  }, [settings.enabled, stopCapture]);

  const testMicrophone = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch {
      return false;
    }
  }, []);

  const value = useMemo<VoiceCommandContextValue>(() => ({
    tenantReady: selectedTenant !== null,
    settings,
    manifest,
    capabilities,
    runtime,
    speechModel,
    models,
    view,
    setSettings,
    toggleListening,
    runText,
    install,
    packageBytes,
    speechModelBytes,
    clearModels,
    confirm,
    cancel,
    dismiss,
    testMicrophone,
  }), [selectedTenant, settings, manifest, capabilities, runtime, speechModel, models, view, setSettings, toggleListening, runText, install, packageBytes, speechModelBytes, clearModels, confirm, cancel, dismiss, testMicrophone]);

  return <VoiceCommandContext.Provider value={value}>{children}</VoiceCommandContext.Provider>;
}

/**
 * The stored preferences, and the one migration between the two versions of them.
 *
 * v1 wrote `voiceLanguage` from the interface language, so a Persian speaker running the
 * English interface has `en` on disk and would go on being transcribed into English however the
 * default changes. The migration carries every other choice across — whether voice is enabled,
 * which checkpoint, the enhanced parser — and drops only that one field, which then comes from
 * `defaultSettings` like it would for a new install.
 *
 * Deliberately not a reset. Losing a 728 MB checkpoint selection to fix a language field would
 * be a poor trade, and the rest of v1 was never wrong.
 */
function readSettings(): LocalAiSettings {
  const stored = readStoredSettings(settingsKey);
  if (stored) {
    return { ...defaultSettings, ...stored, onlineFallback: 'never' };
  }

  const legacy = readStoredSettings(legacySettingsKey);
  if (!legacy) {
    return defaultSettings;
  }

  const carried = { ...legacy };
  delete carried.voiceLanguage;
  return { ...defaultSettings, ...carried, onlineFallback: 'never' };
}

function readStoredSettings(key: string): Partial<LocalAiSettings> | undefined {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'object' && parsed !== null ? parsed as Partial<LocalAiSettings> : undefined;
  }
  catch {
    // A private window, storage switched off, or a value some earlier version wrote badly.
    return undefined;
  }
}

/**
 * What `auto` resolves to: the language of the interface.
 *
 * Only ever reached by explicit choice now. It is no longer the default, because the interface
 * language is a poor proxy for the language somebody speaks — but as a setting an operator
 * picks on purpose, "follow the interface" is a coherent thing to want.
 */
function interfaceVoiceLanguage(): SpokenLanguage {
  const locale = getActiveLocale();
  return locale === 'fa' || locale === 'ar' ? locale : 'en';
}

/** The language to put in Whisper's decoder prompt — `auto` follows the interface. */
function spokenLanguage(setting: VoiceLanguage): SpokenLanguage {
  return setting === 'auto' ? interfaceVoiceLanguage() : setting;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'The local voice command could not be completed.';
}

function isVoiceManifest(value: unknown): value is VoiceManifest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<VoiceManifest>;
  return candidate.schemaVersion === '1.0'
    && typeof candidate.features === 'object'
    && candidate.features !== null
    && typeof candidate.features.enabled === 'boolean'
    && typeof candidate.features.arabicEnabled === 'boolean'
    && Array.isArray(candidate.intents)
    && typeof candidate.models === 'object'
    && candidate.models !== null
    && Array.isArray(candidate.models.models);
}
